import { useEffect, useState } from "react";
import { ArrowDown } from "lucide-react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const CTAS = ["Take a Closer Look", "See It For Yourself", "Go On — Look"];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const yC = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const fade = useTransform(scrollYProgress, [0, 0.14], [1, 0]);
  const rise = useTransform(scrollYProgress, [0, 0.14], [0, -80]);

  const [ctaIndex, setCtaIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCtaIndex((v) => (v + 1) % CTAS.length), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-28 pb-10 pb-[max(2.5rem,env(safe-area-inset-bottom))] min-h-[100svh]"
    >
      <motion.div style={{ y: yC }} className="absolute left-[5%] top-[36%] hidden md:block">
        <span className="absolute h-[2px] w-16 -translate-x-1/2 -translate-y-1/2 bg-ochre" />
        <span className="absolute h-16 w-[2px] -translate-x-1/2 -translate-y-1/2 bg-ochre" />
      </motion.div>

      <motion.div style={{ opacity: fade, y: rise }} className="relative px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-vermilion md:text-xs"
        >
          <span className="inline-block h-2 w-2 bg-vermilion" />
          Pix® — Graphic Design &amp; Marketing
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-[clamp(2.2rem,8.7vw,9.5rem)] font-extrabold uppercase leading-[0.94] tracking-tight"
        >
          <MaskedLine delay={0.3}>Make Yourself</MaskedLine>
          <MaskedLine delay={0.42}>
            Hard to Ignore<span className="text-vermilion">.</span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col gap-10 md:mt-14 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
            className="max-w-md text-base leading-relaxed text-neutral-400 md:text-lg"
          >
            Design and advertising for businesses that want to be impossible
            to ignore.
          </motion.p>

          <motion.button
            data-testid="hero-cta-button"
            layout
            onClick={() => scrollToId("work")}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="group flex w-fit items-center gap-3 rounded-full bg-ivory px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-vermilion hover:text-ivory"
          >
            <span className="relative block h-4 overflow-hidden">
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={CTAS[ctaIndex]}
                  initial={{ y: 16, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -16, opacity: 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="block whitespace-nowrap"
                >
                  {CTAS[ctaIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
            <motion.span
              animate={{ y: [0, 3, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown className="h-4 w-4" />
            </motion.span>
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
